import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { changeColorState, changeNumberState, changeStringState, changeAnchorState, } from "./common_modifiers/properyModifiers";
import { editingDefaults, shapeDefaults, startingLayerList } from "./shapeDefaults";


// Define a type for the slice state
interface ToolState {
    currentTool: ToolType
    snapPoints: { on: boolean, delta: number }
    drawSettings: {
        snapAngle: number,
    },
    lineTypes: LineType[],
    currentLineTypeId: number,
    layerList: Layer[],
    currentLayer: number,
    toolSettings: ToolSettingsCollection
}

const MatrixStart: Mat5 = [
    [1, 0, 0, 0],
    [0, 1, 0, 0],
    [0, 0, 1, 0],
    [0, 0, 0, 1]]
// const defaultToolSymbolLine: LineShape = {
//     type: "line",
//     hitStrokeWidth: 0,
//     closed: false,
//     points: [],
//     dashID: 0,
//     dashEnabled: false,
//     id: 0,
//     stroke: "rgb(200 ,200,200 )",
//     draggable: false,
//     strokeWidth: 2,
//     transformation: MatrixStart,
//     mirrored: false,
//     width: 0,
//     height: 0
// }
//Define the initial state using that type
const initialState: ToolState =
{
    currentTool: "select",
    snapPoints: { on: true, delta: 20 },
    drawSettings: {
        snapAngle: 15,
    },
    toolSettings: {
        symbol: {
            type: "symbol",
            validEditMode: [],
            currentEditMode: "",
            default: shapeDefaults.symbol,
            current: {
                type: "symbol",
                id: 0,
                x: 0,
                y: 0,
                rot: 0,
                scale: 1,
                stroke: 'rgb(10,88,100)',
                draggable: true,
                strokeWidth: 3,
                transformation: MatrixStart,
                mirrored: false,
                width: 0,
                height: 0,
                layerId: 0,
                boundingboxPosition: { x: 0, y: 0 },
                imageKey: "",
                anchor: { horizontal: "left", vertical: "top" }
            },
            functionCategory: "definition"
        },
        select: {
            validEditMode: [],
            currentEditMode: "",
            type: "select",
            functionCategory: "selection",
            current: undefined,
            default: undefined,
        },
        rect_select: {
            validEditMode: [],
            currentEditMode: "",
            functionCategory: "selection",
            current: undefined,
            default: undefined,
            symbolRect: {
                ...editingDefaults.line, type: "rect", width: 0, height: 0,
                x: 0,
                y: 0
            },
            type: "rect_select"
        },
        line: {
            default: shapeDefaults.line,
            current: {
                type: "line",
                id: 0,
                hitStrokeWidth: 3,
                closed: false,
                points: [],
                dashID: 0,
                stroke: 'rgb(10,88,100)',
                draggable: true,
                strokeWidth: 3,
                transformation: MatrixStart,
                mirrored: false,
                width: 0,
                height: 0,
                layerId: 0,
                boundingboxPosition: { x: 0, y: 0 }
            },
            type: "line",
            validEditMode: ["move", "addPoint"],
            currentEditMode: "move",
            functionCategory: "definition",
        },
        polygon: {
            default: shapeDefaults.polygon,
            current: {
                type: "polygon",
                id: 0,
                hitStrokeWidth: 3,
                closed: true,
                points: [],
                dashID: 0,
                stroke: 'rgb(10,88,100)',
                draggable: true,
                strokeWidth: 3,
                transformation: MatrixStart,
                mirrored: false,
                width: 0,
                height: 0,
                layerId: 0,
                boundingboxPosition: { x: 0, y: 0 },
                fillEnabled: true,
                fill: 'rgb(10,88,100)',
            },
            type: "polygon",
            validEditMode: ["move", "addPoint"],
            currentEditMode: "move",
            functionCategory: "definition",
        },
        circle: {
            default: shapeDefaults.circle,
            current: {
                type: "circle",
                hitStrokeWidth: 3,
                strokeWidth: 3,
                radius: 0,
                x: 0,
                y: 0,
                startAngle: 0,
                endAngle: 2 * Math.PI,
                clockwise: false,
                fill: "rgb(150, 150, 150)",
                stroke: 'rgb(10,88,100)',
                fillEnabled: false,
                strokeEnabled: true,
                id: 0,
                draggable: true,
                transformation: MatrixStart,
                mirrored: false,
                width: 0,
                height: 0,
                layerId: 0,
                boundingboxPosition: { x: 0, y: 0 }
            },
            symbolLine: editingDefaults.line,
            type: "circle",
            validEditMode: ["move"],
            currentEditMode: "move",
            functionCategory: "definition",
        },
        text: {
            current: {
                type: "text",
                id: 0,
                uiText: "<Text>",
                fontType: "Arial",
                fontStyle: "",
                fontSize: 20,
                align: "center",
                rotation: 0,
                stroke: "#555",
                draggable: true,
                strokeWidth: 0,
                x: 0,
                y: 0,
                rot: 0,
                lineColor: "rgb(50,50,200)",
                transformation: MatrixStart,
                mirrored: false,
                height: 0,
                width: 0,
                layerId: 0,
                fill: "#000000",
                boundingboxPosition: { x: 0, y: 0 }
            },
            default: shapeDefaults.text,
            type: "text",
            aligmentOptions: ["left", "center", "right"],
            validEditMode: ["move"],
            currentEditMode: "",
            functionCategory: "definition"
        },
        rotation: {
            type: "rotation",
            validEditMode: [],
            currentEditMode: "",
            functionCategory: "editing",
            symbolLine: editingDefaults.line,
            current: undefined,
            default: undefined,
        },
        mirror: {
            type: "mirror",
            validEditMode: [],
            currentEditMode: "",
            functionCategory: "editing",
            symbolLine: editingDefaults.line,
            current: undefined,
            default: undefined,
        },
        move: {
            type: "move",
            validEditMode: [],
            currentEditMode: "",
            functionCategory: "editing",
            symbolLine: editingDefaults.line,
            current: undefined,
            default: undefined,
        },
        split: {
            type: "split",
            symbolLine: editingDefaults.line,
            functionCategory: "editing",
            validEditMode: [],
            currentEditMode: "",
            current: undefined,
            default: undefined,
        }
    },
    currentLineTypeId: 0,
    lineTypes: [],
    layerList: startingLayerList,
    currentLayer: 0
}

export const toolSlice = createSlice({
    name: 'tool',

    initialState,
    reducers: {
        changeTool: (state, action: PayloadAction<ToolType>) => {
            state.currentTool = action.payload
        },
        changeEditMode: (state, action: PayloadAction<{ t: ToolType, e: EditType }>) => {
            state.toolSettings[action.payload.t].currentEditMode = action.payload.e;
        },
        setLineTypes: (state, action: PayloadAction<LineType[]>) => {
            state.lineTypes = action.payload;
        },
        addLineType: (state, action: PayloadAction<LineType | LineType[]>) => {
            console.log("ADDED", action.payload);

            if (Array.isArray(action.payload)) {
                state.lineTypes = state.lineTypes.concat(
                    action.payload.map((a) => {
                        return { ...a, id: state.currentLineTypeId++ }
                    }))
            } else {
                state.lineTypes.push({ ...action.payload, id: state.currentLineTypeId++ })
            }
        },
        changeDefaultNumber: (state, action: PayloadAction<{ id: string, propertyValue: number, propertyName: string }>) => {
            const id = action.payload.id
            if (hasKey(state.toolSettings, id)) {
                const settings = state.toolSettings[id];
                if (settings.functionCategory === "definition") {
                    const find = settings.current;
                    settings.current = changeNumberState<typeof find>(find, action);
                }
            }
        },
        changeDefaultColor: (state, action: PayloadAction<{ id: string, propertyValue: ColorValue, propertyName: string }>) => {
            const id = action.payload.id
            if (hasKey(state.toolSettings, id)) {
                const settings = state.toolSettings[id];
                if (settings.functionCategory === "definition") {
                    const find = settings.current;
                    settings.current = changeColorState<typeof find>(find, action)
                }
            }
        },
        changeDefaultImage: (state, action: PayloadAction<{ id: string, propertyValue: string, propertyName: string }>) => {
            const id = action.payload.id
            if (hasKey(state.toolSettings, id)) {
                const settings = state.toolSettings[id];
                if (settings.functionCategory === "definition") {
                    const find = settings.current;
                    settings.current = changeStringState(find, action)
                }
            }
        },
        changeDefaultAnchor: (state, action: PayloadAction<{ id: string, propertyValue: AnchorType, propertyName: string }>) => {
            const id = action.payload.id
            if (hasKey(state.toolSettings, id)) {
                const settings = state.toolSettings[id];
                if (settings.functionCategory === "definition") {
                    const find = settings.current;
                    settings.current = changeAnchorState(find, action)
                }
            }
        },
        changeDefaultLayer: (state, action: PayloadAction<{ id: string, propertyValue: number, propertyName: string }>) => {
            const id = action.payload.id
            if (hasKey(state.toolSettings, id)) {
                const settings = state.toolSettings[id];
                if (settings.functionCategory === "definition") {
                    const find = settings.current;
                    settings.current = changeNumberState(find, action)
                }
            }
        },
        changeLayerVisiblity: (state, action: PayloadAction<{ id: number }>) => {
            const layer = state.layerList.find((i: Layer) => i.id === action.payload.id)
            if (layer) layer.visibility = !layer?.visibility
        },
        changeLayerList: (state, action: PayloadAction<Layer[]>) => {
            const names = state.layerList.map(x => x.name);
            const newLayers = action.payload.map((layer) => {
                if (names.find(f => f === layer.name)) {
                    return undefined;
                } else {
                    return layer;
                }
            }).filter(x => x !== undefined) as Layer[];

            (newLayers).forEach(
                (layer) => {
                    return toolSlice.caseReducers.addLayer(state, {
                        payload: layer,
                        type: ""
                    });
                }
            )

            state.layerList = action.payload;
        },
        addLayer: (state, action: PayloadAction<Layer>) => {
            const idList = state.layerList.map((a: Layer) => a.id);
            const maxId = Math.max(...idList);
            state.layerList = [...state.layerList, { ...action.payload, id: maxId + 1, visibility: true }]
        },
        deleteLayerForce: (state, payload: PayloadAction<{ deleteId: number }>) => {
            state.layerList = state.layerList.filter((e: Layer) => e.id !== payload.payload.deleteId)
        },
        deleteLayerMoveContent: (state, payload: PayloadAction<{ deleteId: number, moveToId: number }>) => {

        },
        changeDefaultLineType: (state, action: PayloadAction<{ id: string, propertyValue: number, propertyName: string }>) => {
            const id = action.payload.id
            if (hasKey(state.toolSettings, id)) {
                const settings = state.toolSettings[id];
                if (settings.functionCategory === "definition") {
                    const find = settings.current;
                    settings.current = changeNumberState(find, action)
                }
            }
        }
    }
})

function hasKey<O extends ToolSettingsCollection>(obj: O, key: PropertyKey): key is keyof O {
    return key in obj
}

export const {
    deleteLayerForce,
    deleteLayerMoveContent,
    setLineTypes,
    addLayer,
    changeLayerList,
    changeLayerVisiblity,
    changeTool,
    changeEditMode,
    addLineType,
    changeDefaultNumber,
    changeDefaultColor,
    changeDefaultImage,
    changeDefaultAnchor,
    changeDefaultLineType,
    changeDefaultLayer
} = toolSlice.actions

export default toolSlice.reducer

